import React, { useContext, useState } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import moment from 'moment'
import { flowRight, omit, isArray } from 'lodash'
import { withRouter } from 'react-router-dom'
import { withErrorBoundary } from 'lib/errorBoundary'
import { useTranslation } from 'react-i18next'
import { DEFAULT_EVENT_TYPE, EVENT_TYPE, USER_SELECTABLE_EVENT_TYPES } from 'lib/utils/store/event'
import * as Nope from 'lib/utils/validation/Nope'
import { wrapEvent } from 'lib/utils/store/wrappers/event/Event'
import { getVehicleEventsPath } from 'lib/utils/store/vehicle'
import { getPrimaryLocation } from 'lib/location'
import { getLocale } from 'lib/utils/getLocale'

import { EventFormLogicContext } from './EventFormLogic'
import { Form } from 'formik'
import EventLogic from 'components/Events/EventLogic'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import Button from 'components/UI/Button'
import EventFormFields from './EventFormModules'
import { AttachmentsContext } from 'components/Attachments/AttachmentsLogic'
import Icon from 'components/Icon'
import EventFormAttachmentPreview from './EventFormAttachmentPreview/EventFormAttachmentPreview'

import './event-form.pcss'

export const EVENT_FORM_MODE = {
    CREATE_REMINDER: 'CREATE_REMINDER',
    CREATE_EVENT: 'CREATE_EVENT',
    EDIT: 'EDIT',
    CONFIRM: 'CONFIRM',
}

const EventForm = ({ history, location, onSubmitted }) => {
    const {
        initEvent,
        formMode,
        vehicleId,
        renderButtonsPortalTarget,
        onCancel,
        setEventType,
        dirty,
    } = useContext(EventFormLogicContext)

    const { attachments, attachmentsDirty, initAttachments } = useContext(AttachmentsContext)
    const [event] = useState(initEvent)
    const { t, i18n } = useTranslation(['common', 'events'])

    const renderButtons = (form) => {
        const { submitForm, isSubmitting, values, dirty: formDirty } = form
        const { id, formMode } = values

        const isNew = !id
        const isDirty = dirty || formDirty || attachmentsDirty

        if (formMode === EVENT_FORM_MODE.CONFIRM) {
            return (
                <>
                    <Button
                        type="button"
                        className="button"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        trackName="submit"
                        onClick={submitForm}
                    >
                        {t('events:actions.markAsDone')}
                    </Button>
                    {!!onCancel && (
                        <Button
                            trackName="cancel"
                            type="button"
                            className="s-up-only button button--secondary"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            {t('common:cancel')}
                        </Button>
                    )}
                </>
            )
        }

        if (isNew || isDirty) {
            return (
                <>
                    <Button
                        type="button"
                        className="button"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                        trackName="submit"
                        onClick={submitForm}
                    >
                        {t(isNew ? 'common:save' : 'common:saveChanges')}
                    </Button>
                    {!!onCancel && (
                        <Button
                            trackName="cancel"
                            type="button"
                            className="s-up-only button button--secondary"
                            onClick={onCancel}
                            disabled={isSubmitting}
                        >
                            {t('common:cancel')}
                        </Button>
                    )}
                </>
            )
        }

        return (
            <Button
                trackName="close"
                type="button"
                className="s-up-only button"
                onClick={onCancel}
                disabled={isSubmitting}
            >
                {formMode === EVENT_FORM_MODE.CONFIRM ? (
                    <>
                        <Icon fa name="check" color="white" />
                        {t('common:done')}
                    </>
                ) : (
                    t('common:close')
                )}
            </Button>
        )
    }

    const eventType = event.type ? event.type : DEFAULT_EVENT_TYPE

    // use existing event in case we are editing
    // or start from empty to create new event
    const wrappedEvent = wrapEvent(event, eventType)

    const eventValues = omit(wrappedEvent.getValuesObject(), ['attachments'])

    const initialValues = {
        formMode: formMode,
        id: wrappedEvent.getId(),
        type: wrappedEvent.type,
        ...(eventValues), // spread event module default values
    }

    if (!initialValues.eventTime && formMode === EVENT_FORM_MODE.CREATE_EVENT) {
        initialValues.eventTime = eventValues.createdAt
    }

    const handleEventTypeChange = (newType, values) => {
        if (wrappedEvent.type !== newType) {
            setEventType(newType, values)
        }
    }

    const displayAttachmentPreview = (
        formMode === EVENT_FORM_MODE.CREATE_EVENT && initAttachments.length > 0
    )

    const displayTopActions = (
        formMode === EVENT_FORM_MODE.CONFIRM ||
        displayAttachmentPreview
    )

    return (
        <EventLogic>
            {(eventLogic) => (
                <ExtendedFormik
                    key={wrappedEvent.type}
                    id="event-form"
                    initialValues={initialValues}
                    validationSchema={Nope.schema(wrappedEvent.getFormValidationSchema(formMode))}
                    onSubmit={async (values, actions) => {
                        const eventInput = {
                            type: wrappedEvent.type,
                            title: values.title,
                            eventTime: moment(values.eventTime).utc().format('YYYY-MM-DD HH:mm'),
                            mileage: parseInt(values.mileage),
                            comment: values.comment,
                            expense: (values.expense && String(values.expense)) || '',
                            serviceType: null,
                            serviceTypeOtherText: null,
                            attachments: attachments.map(attachment => attachment.id),
                            location: null,
                            inspectionResult: null,
                            inspectionNotices: null,
                        }

                        switch (wrappedEvent.type) {
                        case EVENT_TYPE.SERVICE:
                            if (values.serviceType) eventInput.serviceType = values.serviceType
                            eventInput.serviceTypeOtherText = values.serviceTypeOtherText
                            break
                        case EVENT_TYPE.INSPECTION:
                            if (values.inspectionResult) eventInput.inspectionResult = values.inspectionResult
                            eventInput.inspectionNotices = values.inspectionNotices

                            if (values.trafiInspectionResult) {
                                eventInput.trafiData = eventInput.trafiData || {}
                                eventInput.trafiData.inspectionResult = values.trafiInspectionResult
                            }
                            if (values.trafiInspectionDate) {
                                eventInput.trafiData = eventInput.trafiData || {}
                                eventInput.trafiData.inspectionDate = values.trafiInspectionDate
                            }
                            break
                        case EVENT_TYPE.REFILL:
                            eventInput.refill = eventInput.refill || {}
                            eventInput.refill.fuelPrice = values.fuelPrice || null
                            eventInput.expense = String(values.fuelTotalPrice) || null
                            eventInput.refill.fuelAmount = values.fuelAmount || null
                            eventInput.refill.fuelFilledUp = values.fuelFilledUp
                            break
                        }

                        if (values.remindBefore && values.remindBefore.length > 0) {
                            eventInput.reminders = eventInput.reminders || {}
                            eventInput.reminders.reminderTopic = values.reminderTopic
                            eventInput.reminders.remindBefore = values.remindBefore.map(value => {
                                return {
                                    unit: value.split(' ')[1],
                                    unitAmount: value.split(' ')[0] && parseInt(value.split(' ')[0]),
                                }
                            })
                        }

                        if (values.nextReminderTime && values.nextRemindBefore && values.nextRemindBefore.length > 0) {
                            eventInput.reminders = eventInput.reminders || {}
                            eventInput.reminders.reminderTime = moment(values.nextReminderTime).utc().format('YYYY-MM-DD HH:mm')
                            eventInput.reminders.reminderTopic = values.nextReminderTopic
                            eventInput.reminders.remindBefore = values.nextRemindBefore.map(value => {
                                return {
                                    unit: value.split(' ')[1],
                                    unitAmount: value.split(' ')[0] && parseInt(value.split(' ')[0]),
                                }
                            })
                        }

                        if (values.locationLatitude || values.locationLongitude) {
                            eventInput.location = eventInput.location || {}
                            eventInput.location.latitude = values.locationLatitude
                            eventInput.location.longitude = values.locationLongitude
                        }

                        if (values.locationName) {
                            eventInput.location = eventInput.location || {}
                            eventInput.location.locationName = values.locationName
                            eventInput.location.vicinity = values.locationVicinity
                            eventInput.location.googlePlaceId = values.locationGooglePlaceId
                            eventInput.location.googlePlaceUrl = values.locationGooglePlaceUrl
                            eventInput.location.addressFormatted = values.locationAddressFormatted
                            eventInput.location.addressComponents = values.locationAddressComponents
                        }

                        if (values.formMode === EVENT_FORM_MODE.CONFIRM) {
                            eventInput.confirmEvent = true
                        }

                        const isUpdate = wrappedEvent.id
                        const isReminder = wrappedEvent.isPending() || moment(eventInput.eventTime).isAfter(Date.now())

                        const mutationCall = isUpdate
                            ? eventLogic.updateEvent(wrappedEvent.id, eventInput, isReminder)
                            : eventLogic.addEvent(vehicleId, eventInput, isReminder)

                        await mutationCall
                            .then((result) => {
                                // there can be more than one event returned when saving
                                const eventResult = result.data.addEvent || result.data.updateEvent
                                const event = isArray(eventResult) ? eventResult[0] : eventResult

                                if (onSubmitted) {
                                    const skipDefaultSubmit = onSubmitted({
                                        values: values,
                                        actions: actions,
                                        result: result,
                                        event: event,
                                    })

                                    if (skipDefaultSubmit) return
                                }

                                actions.setSubmitting(false)

                                // After saving event, check if user is not on events page
                                // and needs to be redirected to there
                                const redirectUrl = `${getLocale(i18n.language)}${getVehicleEventsPath(vehicleId)}`
                                const primaryLocation = getPrimaryLocation(location)
                                if (primaryLocation.pathname !== redirectUrl) {
                                    history.push(redirectUrl)
                                    return
                                }

                                if (isUpdate) {
                                    history.push({
                                        ...primaryLocation,
                                        hash: '',
                                    })
                                } else {
                                    history.push({
                                        ...primaryLocation,
                                        hash: '',
                                        state: {
                                            ...primaryLocation.state,
                                            timeline: {
                                                newEvent: {
                                                    id: event.id,
                                                }
                                            }
                                        }
                                    })
                                }
                            })
                            .catch(() => {
                                actions.setSubmitting(false)
                            })
                    }}
                >
                    {(form) => {
                        const { values } = form

                        const formButtons = renderButtons(form)

                        return (
                            <Form noValidate className="event-form">
                                {displayAttachmentPreview && (
                                    <EventFormAttachmentPreview attachment={initAttachments[0]} />
                                )}
                                {displayTopActions && (
                                    <div className="action-wrapper display-s-up">
                                        {formButtons}
                                    </div>
                                )}
                                {(eventType === EVENT_TYPE.SYSTEM || [EVENT_FORM_MODE.CREATE_REMINDER, EVENT_FORM_MODE.CONFIRM].includes(formMode)) ? (
                                    <h2 className="event-form__title">{wrappedEvent.getDisplayTitle(t)}</h2>
                                ) : (
                                    <select
                                        className="event-form__type-select"
                                        onChange={(e) => handleEventTypeChange(e.target.value, values)}
                                        onBlur={(e) => handleEventTypeChange(e.target.value, values)}
                                        defaultValue={wrappedEvent.type}
                                    >
                                        {USER_SELECTABLE_EVENT_TYPES.map(eventType => (
                                            <option
                                                key={eventType}
                                                value={eventType}
                                            >
                                                {t(`events:${eventType}.title`)}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                <EventFormFields
                                    form={form}
                                    wrappedEvent={wrappedEvent}
                                />
                                {renderButtonsPortalTarget && (
                                    ReactDOM.createPortal(
                                        formButtons,
                                        renderButtonsPortalTarget
                                    )
                                )}
                                <div className="action-wrapper display-s-up">
                                    {formButtons}
                                </div>
                            </Form>
                        )
                    }}
                </ExtendedFormik>
            )}
        </EventLogic>
    )
}

EventForm.propTypes = {
    onSubmitted: PropTypes.func,
}

export default flowRight(
    withErrorBoundary,
    withRouter,
)(EventForm)
